<script setup lang="ts">
import { ref, computed } from 'vue'
import { Timestamp } from 'firebase/firestore'
import type { Personnel } from '@/types'
import { PersonnelStatus } from '@/types'
import PersonnelDetailDialog from '@/components/personnel/PersonnelDetailDialog.vue'
import PersonnelTooltip from '@/components/personnel/PersonnelTooltip.vue'

const props = defineProps<{
  person: Personnel
  teamIdentifier?: string | null
  disabled?: boolean
}>()

const dragStartX = ref(0)
const dragStartY = ref(0)

function onMouseDown(e: MouseEvent) {
  dragStartX.value = e.clientX
  dragStartY.value = e.clientY
}

function onMouseUp(e: MouseEvent) {
  if (props.disabled) return
  const dx = Math.abs(e.clientX - dragStartX.value)
  const dy = Math.abs(e.clientY - dragStartY.value)
  if (dx < 5 && dy < 5) {
    showDetails.value = true
  }
}

const statusBg: Record<PersonnelStatus, string> = {
  [PersonnelStatus.Available]: 'border-green-300 dark:border-green-700',
  [PersonnelStatus.Assigned]: 'border-blue-300 dark:border-blue-700',
  [PersonnelStatus.OutOfService]: 'border-slate-300 dark:border-surface-600',
}

const statusDot: Record<PersonnelStatus, string> = {
  [PersonnelStatus.Available]: 'bg-green-500',
  [PersonnelStatus.Assigned]: 'bg-blue-500',
  [PersonnelStatus.OutOfService]: 'bg-slate-400',
}

const statusLabel: Record<PersonnelStatus, string> = {
  [PersonnelStatus.Available]: 'Available',
  [PersonnelStatus.Assigned]: 'Assigned',
  [PersonnelStatus.OutOfService]: 'Out of Service',
}

const showDetails = ref(false)

function formatTime(ts: Timestamp | null): string {
  if (!ts) return '—'
  const d = ts.toDate()
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

const allQuals = computed((): string[] => {
  const q = props.person.qualifications
  const result: string[] = []
  if (q.searcher_type && q.searcher_type !== 'None') result.push(q.searcher_type as string)
  if (q.rope_qual && q.rope_qual !== 'None') result.push(q.rope_qual as string)
  if (q.medical_qual && q.medical_qual !== 'None') result.push(q.medical_qual as string)
  if (Array.isArray(q.misc_quals)) result.push(...(q.misc_quals as string[]))
  return result
})
</script>

<template>
  <div
    :data-person-name="`${person.first} ${person.last}`"
    class="@container bg-surface-container p-3 pt-2 select-none rounded-md"
    :class="[statusBg[person.status], disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-grab active:cursor-grabbing']"
    @mousedown="onMouseDown"
    @mouseup="onMouseUp"
  >
  <PersonnelTooltip :person="person" side="right">
    <!-- Top row: name (left) + agency + time_in (right) -->
    <div class="flex items-start justify-between gap-2">
      
        <div class="flex items-center gap-2">
            <div class="w-2 h-2 rounded-full" :class="statusDot[person.status]"></div>
            <p class="text-sm font-medium text-on-surface truncate">{{ person.first }} {{ person.last }}</p>
        </div>
        <div class="shrink-0 flex items-center gap-1.5 mt-0.5">
          <span class="hidden @[250px]:flex text-[10px] font-label uppercase tracking-widest text-outline truncate text-right">{{ person.agency }}</span>
          <span class="hidden @[400px]:inline-block text-[10px] font-mono text-outline">{{ formatTime(person.time_in) }}</span>
        </div>
      
    </div>
    <!-- Qual chips row: only visible when card width >= 320px -->
    <div v-if="allQuals.length > 0" class="hidden @[400px]:flex flex-wrap gap-1 mt-1.5">
      <span
        v-for="(qual, i) in allQuals"
        :key="`${i}-${qual}`"
        class="text-[9px] font-mono uppercase tracking-wide bg-surface-container-high border border-border px-1.5 py-0.5 rounded"
      >{{ qual }}</span>
    </div>
    </PersonnelTooltip>
    <PersonnelDetailDialog v-model:visible="showDetails" :person="person" />
  </div>
</template>
